import { isPortfolioCompleted } from '@/utils/portfolioStatus';
import { getPortfolioSize, updatePortfolioFields } from '@/services/portfolio/PortfolioService';
import { fetchAllocationsForCompletion } from '@/services/portfolio/PortfolioAllocationsService';

/**
 * Recompute and persist portfolio.status based on current DB allocations and portfolio_size.
 * Returns the status string written to DB, or null if no change was required.
 */
export async function recomputeAndPersistPortfolioStatus(portfolioId: string): Promise<'completed' | 'in_progress' | null> {
  if (!portfolioId) return null as any;

  // Fetch size + allocations via services for source-of-truth
  const [sizeRaw, allocs] = await Promise.all([
    getPortfolioSize(portfolioId),
    fetchAllocationsForCompletion(portfolioId),
  ]);

  const size = Number(sizeRaw ?? 0);
  const completed = isPortfolioCompleted({ portfolio_size: size, portfolio_allocations: allocs as any });
  const nextStatus: 'completed' | 'in_progress' = completed ? 'completed' : 'in_progress';

  // Note: We do not fetch current status here to avoid extra round trip; updating to same status is harmless.
  await updatePortfolioFields(portfolioId, { status: nextStatus, updated_at: new Date().toISOString() });
  return nextStatus;
}

// Debounce map keyed by portfolioId to coalesce rapid edits
const recomputeTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Schedule a debounced portfolio status recompute. Subsequent calls within `delayMs`
 * reset the timer so only one DB write occurs after a burst of edits.
 */
export function schedulePortfolioStatusRecompute(portfolioId: string, delayMs = 400) {
  if (!portfolioId) return;
  const existing = recomputeTimers.get(portfolioId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(async () => {
    recomputeTimers.delete(portfolioId);
    try {
      await recomputeAndPersistPortfolioStatus(portfolioId);
    } catch (err) {
      // Swallow errors to avoid noisy UI; underlying operations already have toasts/logging
      console.warn('[status-updater] recompute failed (debounced)', err);
    }
  }, delayMs);
  recomputeTimers.set(portfolioId, timer);
}
